import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { num, secondsToDays, fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';
import { useT, format } from '../i18n';

const SEC_PER_PT = 28800;

// Item-Level-Werte > THRESHOLD gelten als unplausibel. Zur Erinnerung:
// die Säulen im Funnel cappen ohnehin auf 10 PT pro Item — diese
// Tabelle macht sichtbar, WELCHE Items das sind, damit JIRA gepflegt
// werden kann (Sub-Task-Aggregat-Fehler, Schreibfehler im Estimate, …).
const THRESHOLD_OPTIONS = [20, 30, 50, 100] as const;

type Outlier = {
  key: string;
  type: string;
  team: string;
  status: string;
  summary: string;
  resolved: string;
  fixVersions: string;
  estPT: number;
  sumTsPT: number;
  itemTsPT: number;
  maxPT: number;
};

export function OutlierTickets() {
  const t = useT();
  const rows = useData(s => s.rows);
  const teamFilter = useFilters(s => s.team);
  const [threshold, setThreshold] = useState<number>(30);

  const items = useMemo<Outlier[]>(() => {
    const out: Outlier[] = [];
    for (const r of rows) {
      const type = r['Issue Type'] ?? '';
      if (type !== 'Story' && type !== 'Bug') continue;
      if (teamFilter && r['Team Name'] !== teamFilter) continue;

      const estPT    = secondsToDays(r['Σ Original Estimate']) ?? 0;
      const sumTsSec = num(r['Σ Time Spent'])  ?? 0;
      const itemTsSec= num(r['Time Spent'])    ?? 0;
      const sumTsPT  = sumTsSec  > 0 ? sumTsSec  / SEC_PER_PT : 0;
      const itemTsPT = itemTsSec > 0 ? itemTsSec / SEC_PER_PT : 0;
      const maxPT = Math.max(estPT, sumTsPT, itemTsPT);
      if (maxPT <= threshold) continue;

      const fvRaw = r['Fix versions'];
      const fvList = Array.isArray(fvRaw) ? fvRaw : (fvRaw ? [fvRaw] : []);
      out.push({
        key:    r['Issue key'] ?? '?',
        type,
        team:   r['Team Name'] ?? '–',
        status: r['Status']    ?? '–',
        summary: r['Summary']  ?? '',
        resolved: r['Resolved'] ?? '',
        fixVersions: fvList.join(', '),
        estPT,
        sumTsPT,
        itemTsPT,
        maxPT,
      });
    }
    out.sort((a, b) => b.maxPT - a.maxPT);
    return out;
  }, [rows, teamFilter, threshold]);

  // Hochrechnung: was nimmt der Cap raus?
  const totalRaw     = items.reduce((s, x) => s + Math.max(x.estPT, x.sumTsPT, x.itemTsPT), 0);
  const totalAbove10 = items.reduce((s, x) => s + Math.max(0, Math.max(x.estPT, x.sumTsPT, x.itemTsPT) - 10), 0);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <WarningAmberIcon sx={{ color: 'warning.main' }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {format(t.outliers.title, { threshold })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t.outliers.explain}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          value={threshold}
          exclusive
          onChange={(_, v) => v != null && setThreshold(v)}
        >
          {THRESHOLD_OPTIONS.map(v => (
            <ToggleButton key={v} value={v}>&gt;{v} PT</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={items.length === 0 ? 'success' : 'warning'}
          label={format(t.outliers.itemsOverThreshold, { count: items.length })}
        />
        {items.length > 0 && (
          <>
            <Chip size="small" variant="outlined" label={format(t.outliers.sumRawPT, { value: fmtInt(totalRaw) })} />
            <Chip size="small" variant="outlined" label={format(t.outliers.cappedRemoved, { value: fmtInt(totalAbove10) })} />
          </>
        )}
        {teamFilter && (
          <Chip size="small" color="primary" label={`${t.banner.teamPrefix}: ${teamFilter}`} />
        )}
      </Stack>

      {items.length === 0 ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {teamFilter
              ? format(t.outliers.emptyStateTeam, { threshold, team: teamFilter })
              : format(t.outliers.emptyState, { threshold })}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fix Versions</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Σ Estimate</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Σ Time Spent</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">TS (Item)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">max PT</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Resolved</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(it => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{it.key}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={it.type}
                      color={it.type === 'Story' ? 'success' : 'error'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: 10.5 }}
                    />
                  </TableCell>
                  <TableCell>{it.team}</TableCell>
                  <TableCell><StatusChip status={it.status} /></TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: 'text.secondary' }}>{it.fixVersions || '—'}</TableCell>
                  <NumCell pt={it.estPT} />
                  <NumCell pt={it.sumTsPT} />
                  <NumCell pt={it.itemTsPT} />
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: it.maxPT > 100 ? 'error.main' : it.maxPT > 50 ? 'warning.main' : 'text.primary' }}>
                    {Math.round(it.maxPT)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>{it.resolved || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 11.5, maxWidth: 420 }}>
                    <Tooltip arrow title={it.summary || '—'}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.summary || '—'}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Lese-Hilfe: Σ Estimate = Plan-Wert auf Item-Ebene · Σ Time Spent aggregiert geloggte
        Zeit über Sub-Tasks (Hauptquelle für unplausible Werte) · TS (Item) wird im SchueCal
        praktisch nie gepflegt. Der Cap=10 PT in den Säulen verhindert, dass diese Outlier den
        Gesamt-Ist verzerren — die Tabelle zeigt sie für die JIRA-Pflege trotzdem im Original.
      </Typography>
    </Box>
  );
}

function NumCell({ pt }: { pt: number }) {
  if (pt <= 0) return <TableCell align="right" sx={{ color: 'text.disabled' }}>–</TableCell>;
  return (
    <TableCell
      align="right"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        color: pt > 100 ? 'error.main' : pt > 30 ? 'warning.main' : 'text.primary',
        fontWeight: pt > 30 ? 700 : 400,
      }}
    >
      {Math.round(pt)}
    </TableCell>
  );
}
